/**
 * Forces Yotpo star rating on collection product cards to one line at 12px.
 * Yotpo injects markup asynchronously (sometimes in shadow roots).
 */

const CARD_SELECTOR = '.product-card-yotpo-rating';
const STYLE_ID = 'product-card-yotpo-rating-styles';

const INLINE_CSS = `
  :host, .yotpo, .yotpo * {
    font-size: 12px !important;
    line-height: 1.2 !important;
  }

  .yotpo-bottomline,
  .standalone-bottomline,
  [class*="bottom-line"],
  [class*="bottomline"],
  [class*="star-rating"] {
    display: inline-flex !important;
    flex-direction: row !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
    gap: 4px !important;
    margin: 0 !important;
    font-size: 12px !important;
    line-height: 1.2 !important;
  }

  .yotpo-bottomline > *,
  .standalone-bottomline > *,
  [class*="bottom-line"] > *,
  [class*="bottomline"] > * {
    display: inline-flex !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .yotpo-stars,
  [class*="yotpo-star"],
  .star-container {
    display: inline-flex !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
    gap: 2px !important;
    margin: 0 !important;
  }

  .yotpo-bottomline a,
  .yotpo-bottomline .text-m,
  .yotpo a,
  .yotpo span,
  .yotpo p,
  [class*="reviews"] {
    font-size: 12px !important;
    line-height: 1.2 !important;
    white-space: nowrap !important;
    margin: 0 !important;
  }

  .yotpo-icon-star,
  .yotpo-icon-half-star,
  [class*="yotpo-star"][class*="full"],
  .star-container svg path {
    color: #f9a31a !important;
    fill: #f9a31a !important;
  }

  .yotpo-icon-empty-star,
  [class*="yotpo-star"][class*="empty"] {
    color: #9ca3af !important;
    fill: #9ca3af !important;
  }

  .yotpo-icon-star,
  .yotpo-icon-empty-star,
  .yotpo-icon-half-star,
  .star-container svg {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
    max-width: 12px !important;
    max-height: 12px !important;
  }
`;

/**
 * @param {ShadowRoot | Document | Element} root
 */
function injectStyles(root) {
  if (root instanceof ShadowRoot) {
    if (root.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = INLINE_CSS;
    root.appendChild(style);
    return;
  }

  if (root instanceof Document) return;

  if (!(root instanceof HTMLElement)) return;
  const element = root;
  element.style.display = 'inline-flex';
  element.style.flexDirection = 'row';
  element.style.alignItems = 'center';
  element.style.flexWrap = 'nowrap';
  element.style.gap = '4px';
  element.style.fontSize = '12px';
  element.style.lineHeight = '1.2';
  element.style.margin = '0';
}

/**
 * @param {HTMLElement} wrapper
 */
function styleWrapper(wrapper) {
  injectStyles(wrapper);

  wrapper.style.marginTop = '0';
  wrapper.style.marginBottom = '0.25rem';
  wrapper.style.paddingTop = '0';
  wrapper.style.minHeight = '0';

  const customLiquidHost = wrapper.parentElement;
  if (customLiquidHost?.parentElement?.classList.contains('product-card__content')) {
    customLiquidHost.style.marginTop = '-2px';
    customLiquidHost.style.marginBottom = '0';
  }

  wrapper.querySelectorAll('.yotpo-widget-instance').forEach((widget) => {
    if (widget.shadowRoot) {
      injectStyles(widget.shadowRoot);
    }
    injectStyles(widget);
  });

  // Keep promo badge hidden without relying on template custom_css length budget.
  wrapper.querySelectorAll('.iom-discount-badge').forEach((badge) => {
    if (badge instanceof HTMLElement) {
      badge.style.display = 'none';
    }
  });

  const layoutSelectors = [
    '.yotpo-bottomline',
    '.standalone-bottomline',
    '[class*="bottom-line"]',
    '[class*="bottomline"]',
    '[class*="star-rating"]',
    '.yotpo',
  ];

  layoutSelectors.forEach((selector) => {
    wrapper.querySelectorAll(selector).forEach((node) => {
      injectStyles(node);
    });
  });

  wrapper.querySelectorAll('a, span, p, div').forEach((node) => {
    if (
      node instanceof HTMLElement &&
      node.closest('.yotpo, .yotpo-widget-instance, [class*="yotpo"]')
    ) {
      node.style.fontSize = '12px';
      node.style.lineHeight = '1.2';
    }
  });

  const filledStarSelectors = '.yotpo-icon-star, .yotpo-icon-half-star, [class*="yotpo-star"][class*="full"]';
  const emptyStarSelectors = '.yotpo-icon-empty-star, [class*="yotpo-star"][class*="empty"]';

  wrapper.querySelectorAll(filledStarSelectors).forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.color = '#f9a31a';
      node.style.fill = '#f9a31a';
    }
  });

  wrapper.querySelectorAll(emptyStarSelectors).forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.color = '#9ca3af';
      node.style.fill = '#9ca3af';
    }
  });

  wrapper.querySelectorAll('.star-container svg path').forEach((path) => {
    if (!(path instanceof SVGPathElement)) return;
    const starRoot = path.closest('.yotpo-icon-star, .yotpo-icon-half-star, [class*="full"]');
    path.style.fill = starRoot ? '#f9a31a' : '#9ca3af';
  });
}

function styleAllCards() {
  document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
    if (card instanceof HTMLElement) {
      styleWrapper(card);
    }
  });
}

function observeCards() {
  styleAllCards();

  const observer = new MutationObserver(() => {
    styleAllCards();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('yotpoJSReady', styleAllCards);
  document.addEventListener('shopify:section:load', styleAllCards);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCards);
} else {
  observeCards();
}

window.setTimeout(styleAllCards, 500);
window.setTimeout(styleAllCards, 1500);
window.setTimeout(styleAllCards, 3000);
